import { Routes } from "@angular/router";
import { userAuthCanMatchGuard, userGuestCanMatchGuard } from "./features/auth";

export const routes: Routes = [
    // ADMIN
    {
        path: "admin",
        loadChildren: () => import("./features/admin/admin.routes").then((m) => m.ADMIN_ROUTES),
    },
    {
        path: "",
        loadComponent: () => import("./layouts/user-layout/user-layout.component").then((m) => m.UserLayoutComponent),
        children: [
            {
                path: "",
                pathMatch: "full",
                redirectTo: "home",
            },
            {
                path: "home",
                loadComponent: () =>
                    import("./features/home-page/home-page.component").then((m) => m.HomePageComponent),
            },
            {
                path: "about-us",
                loadComponent: () => import("./features/about-us/about-us.component").then((m) => m.AboutUsComponent),
            },
            {
                path: "contact",
                loadComponent: () => import("./contact/contact.component").then((m) => m.ContactComponent),
            },
            {
                path: "products",
                loadComponent: () =>
                    import("./shared/product-viewlist/product-viewlist.component").then(
                        (m) => m.ProductViewlistComponent,
                    ),
            },
            {
                path: "products/:id/reviews",
                loadComponent: () =>
                    import("./product-detail-page/product-review-page/product-review-page.component").then(
                        (m) => m.ProductReviewPageComponent,
                    ),
            },
            {
                path: "products/:id",
                loadComponent: () =>
                    import("./product-detail-page/product-detail-page.component").then(
                        (m) => m.ProductDetailPageComponent,
                    ),
            },
            {
                path: "cart",
                loadComponent: () =>
                    import("./dashboard/cart-page/cart-page.component").then((m) => m.CartPageComponent),
            },
            {
                path: "wishlist",
                loadComponent: () =>
                    import("./shared/wishlist-page/wishlist-page.component").then((m) => m.WishlistPageComponent),
                canMatch: [userAuthCanMatchGuard],
            },
            {
                path: "address",
                loadComponent: () =>
                    import("./dashboard/checkout-page/address-list/address-list.component").then(
                        (m) => m.AddressListComponent,
                    ),
            },
            {
                path: "order/success",
                loadComponent: () =>
                    import("./order-success/order-success.component").then((m) => m.OrderSuccessComponent),
                canMatch: [userAuthCanMatchGuard],
            },
            {
                path: "order/success/:merchantTransactionId",
                loadComponent: () =>
                    import("./order-success/order-success.component").then((m) => m.OrderSuccessComponent),
                canMatch: [userAuthCanMatchGuard],
            },
            {
                path: "terms-and-conditions",
                loadComponent: () =>
                    import("./shared/footer/term-condition/term-condition.component").then(
                        (m) => m.TermConditionComponent,
                    ),
            },
            {
                path: "privacy-and-policy",
                loadComponent: () =>
                    import("./shared/footer/privacy-page/privacy-page.component").then((m) => m.PrivacyPageComponent),
            },
            {
                path: "notifications",
                loadComponent: () =>
                    import("./user-notification/user-notification.component").then((m) => m.UserNotificationComponent),
                canMatch: [userAuthCanMatchGuard],
            },
        ],
    },
    {
        path: "auth",
        loadComponent: () => import("./layouts/auth-layout/auth-layout.component").then((m) => m.AuthLayoutComponent),
        children: [
            {
                path: "login",
                canMatch: [userGuestCanMatchGuard],
                loadComponent: () =>
                    import("./features/auth/login-page/login-page.component").then((m) => m.LoginPageComponent),
            },
        ],
    },
    {
        path: "",
        loadComponent: () =>
            import("./layouts/plain-layout/plain-layout.component").then((m) => m.PlainLayoutComponent),
        children: [
            {
                path: "account",
                loadComponent: () => import("./profile/profile.component").then((m) => m.ProfileComponent),
                canMatch: [userAuthCanMatchGuard],
                children: [
                    {
                        path: "overview",
                        loadComponent: () =>
                            import("./profile/overview/overview.component").then((m) => m.OverviewComponent),
                    },
                    {
                        path: "profileDetails",
                        loadComponent: () =>
                            import("./profile/profile-details/profile-details.component").then(
                                (m) => m.ProfileDetailComponent,
                            ),
                    },
                    {
                        path: "profileDetails/:id",
                        loadComponent: () =>
                            import("./profile/profile-details/profile-form/profile-form.component").then(
                                (m) => m.ProfileFormComponent,
                            ),
                    },
                    {
                        path: "addressDetails",
                        loadComponent() {
                            return import("./profile/address-details/address-details.component").then(
                                (m) => m.AddressDetailsComponent,
                            );
                        },
                    },
                    {
                        path: "rewards",
                        loadComponent() {
                            return import("./rewards/rewards.component").then((m) => m.RewardsComponent);
                        },
                    },
                    {
                        path: "myStructures",
                        loadComponent() {
                            return import("./my-structures/my-structures.component").then(
                                (m) => m.MyStructuresComponent,
                            );
                        },
                    },
                    {
                        path: "trackOrder",
                        loadComponent() {
                            return import("./shared/track-order/track-order.component").then(
                                (m) => m.TrackOrderComponent,
                            );
                        },
                    },
                    {
                        path: "orders/:id/orderInfo",
                        loadComponent() {
                            return import("./profile/order-info/order-info.component").then(
                                (m) => m.OrderInfoComponent,
                            );
                        },
                    },
                    {
                        path: "orders/:id/track",
                        loadComponent() {
                            return import("./profile/order-status/order-status.component").then(
                                (m) => m.OrderStatusComponent,
                            );
                        },
                    },
                    {
                        path: "orders/:orderId/itemsReview",
                        loadComponent() {
                            return import("./profile/user-review-page/user-review-page.component").then(
                                (m) => m.UserReviewPageComponent,
                            );
                        },
                    },
                    {
                        path: "orders/:id/itemsReview/:itemId/review",
                        loadComponent() {
                            return import("./order-details/rating-and-review/rating-and-review.component").then(
                                (m) => m.RatingAndReviewComponent,
                            );
                        },
                    },
                    {
                        path: "orders/:id/:action",
                        loadComponent() {
                            return import("./profile/item-list/item-list.component").then((m) => m.ItemListComponent);
                        },
                        canMatch: [userAuthCanMatchGuard],
                    },
                    {
                        path: "orders/:id/:action/reason",
                        loadComponent() {
                            return import("./profile/return-replace-page/return-replace-page.component").then(
                                (m) => m.ReturnReplacePageComponent,
                            );
                        },
                        canMatch: [userAuthCanMatchGuard],
                    },
                    {
                        path: "orders/:id/:action/reason/success",
                        loadComponent() {
                            return import("./profile/return-exchange-success/return-exchange-success.component").then(
                                (m) => m.ReturnExchangeSuccessComponent,
                            );
                        },
                        canMatch: [userAuthCanMatchGuard],
                    },
                ],
            },
            {
                path: "userDetails",
                loadComponent: () =>
                    import("./user-details/user-details.component").then((m) => m.UserDetailsComponent),
                canMatch: [userAuthCanMatchGuard],
            },
            {
                path: "designer",
                loadComponent() {
                    return import("./designer/designer.component").then((m) => m.DesignerComponent);
                },
                canMatch: [userAuthCanMatchGuard],
            },
            {
                path: "designer/:id",
                loadComponent() {
                    return import("./designer/designer.component").then((m) => m.DesignerComponent);
                },
            },
        ],
    },
    {
        path: "404",
        loadComponent: () =>
            import("./shared/page-not-found/page-not-found.component").then((m) => m.PageNotFoundComponent),
    },
    {
        path: "**",
        redirectTo: "404",
    },
];
